<script>
import {defineComponent} from 'vue'

export default defineComponent({
  name: "TicketSidebar",
  props: {
    title: {
      type: String,
      default: 'Sidebar'
    },
    item: {
      type: Object,
      default: null
    },
    fields: {
      type: Array,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'form', // 'form' | 'custom'
      validator: (value) => ['form', 'custom'].includes(value)
    }
  },
  data() {
    return {
      formData: {}
    }
  },
  methods: {
    handleSubmit() {
      this.$emit('save', this.formData)
    },
    handleClose() {
      this.$root.$emit('bv::toggle::collapse', 'sidebar-right')
    },
    handleHidden() {
      this.resetForm()
      this.$emit('close')
    },
    resetForm() {
      this.formData = {}
    },
    initFormData() {
      if (this.item) {
        this.formData = {...this.item}
      } else {
        this.formData = this.fields.reduce((acc, field) => {
          acc[field.key] = field.default !== undefined ? field.default : null
          return acc
        }, {})
      }
    }
  },
  watch: {
    item: {
      immediate: true,
      handler() {
        this.initFormData()
      }
    }
  },
})
</script>

<template>
  <div>
    <b-sidebar backdrop id="sidebar-right" :title="$props.title" no-header
               :width="this.$props.mode === 'custom' ? '530px' : '320px'" right shadow @hidden="handleHidden">
      <header class="ts-head">
        <h5 class="ts-head__title">{{ title }}</h5>
        <button type="button" class="ts-close" @click.stop="handleClose"
                v-b-tooltip.hover.left="'Закрыть (Esc)'">
          <i class="fa fa-times"></i>
        </button>
      </header>
      <div class="sidebar-body">
        <div v-if="isLoading" class="d-flex justify-content-center align-items-center h-100">
          <b-spinner></b-spinner>
        </div>
        <div v-else-if="mode === 'custom'" class="sidebar-custom-wrapper">
          <slot name="custom-content" :item="item" :close="handleClose"></slot>
        </div>
        <b-form @submit.prevent="handleSubmit" v-else>
          <div v-for="field in fields" :key="field.key" class="mb-3">
            <!-- Text Input -->
            <b-form-group
                v-if="field.type === 'text' || !field.type"
                :label="field.label"
                :label-for="`field-${field.key}`"
            >
              <b-form-input
                  :id="`field-${field.key}`"
                  v-model="formData[field.key]"
                  :placeholder="field.placeholder || field.label"
                  :required="field.required"
              />
            </b-form-group>

            <!-- Textarea -->
            <b-form-group
                v-else-if="field.type === 'textarea'"
                :label="field.label"
                :label-for="`field-${field.key}`"
            >
              <b-form-textarea
                  :id="`field-${field.key}`"
                  v-model="formData[field.key]"
                  :placeholder="field.placeholder || field.label"
                  :required="field.required"
                  rows="3"
              />
            </b-form-group>

            <!-- Select -->
            <b-form-group
                v-else-if="field.type === 'select'"
                :label="field.label"
                :label-for="`field-${field.key}`"
            >
              <b-form-select
                  :id="`field-${field.key}`"
                  v-model="formData[field.key]"
                  :options="field.options"
                  :required="field.required"
              >
                <template #first v-if="field.placeholder">
                  <b-form-select-option :value="null" disabled>
                    {{ field.placeholder }}
                  </b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>

            <!-- Checkbox -->
            <b-form-group v-else-if="field.type === 'checkbox'">
              <b-form-checkbox
                  :id="`field-${field.key}`"
                  v-model="formData[field.key]"
                  :value="true"
                  :unchecked-value="false"
              >
                {{ field.label }}
              </b-form-checkbox>
            </b-form-group>

            <!-- Number -->
            <b-form-group
                v-else-if="field.type === 'number'"
                :label="field.label"
                :label-for="`field-${field.key}`"
            >
              <b-form-input
                  :id="`field-${field.key}`"
                  v-model="formData[field.key]"
                  type="number"
                  :placeholder="field.placeholder || field.label"
                  :required="field.required"
                  :min="field.min"
                  :max="field.max"
              />
            </b-form-group>

            <!-- Date -->
            <b-form-group
                v-else-if="field.type === 'date'"
                :label="field.label"
                :label-for="`field-${field.key}`"
            >
              <b-form-input
                  :id="`field-${field.key}`"
                  v-model="formData[field.key]"
                  type="date"
                  :required="field.required"
              />
            </b-form-group>

            <!-- Color -->
            <b-form-group
                v-else-if="field.type === 'color'"
                :label="field.label"
                :label-for="`field-${field.key}`"
            >
              <div class="d-flex align-items-center gap-2">
                <b-form-input
                    :id="`field-${field.key}`"
                    v-model="formData[field.key]"
                    type="color"
                    :required="field.required"
                    style="width: 60px; height: 40px;"
                />
                <b-form-input
                    v-model="formData[field.key]"
                    type="text"
                    :placeholder="field.placeholder || '#000000'"
                    :required="field.required"
                    pattern="^#[0-9A-Fa-f]{6}$"
                    class="flex-grow-1"
                />
              </div>
              <small class="text-muted">Формат: #RRGGBB</small>
            </b-form-group>
          </div>
          <div class="ts-foot">
            <button type="button" class="ts-btn ts-btn--ghost" @click="handleClose">Отмена</button>
            <button type="submit" class="ts-btn ts-btn--primary">
              <i class="fa fa-check"></i> Сохранить
            </button>
          </div>
        </b-form>
      </div>
    </b-sidebar>
  </div>
</template>

<style scoped lang="scss">
@import "~@/assets/scss/tournament-form-tokens";

#sidebar-right {
  overflow-y: hidden;
}

:deep(.b-sidebar) {
  @include tf-tokens-light;

  border-left: 1px solid var(--tf-border-strong);
  box-shadow: -12px 0 40px rgba(16, 20, 30, .18);
}

:deep(.b-sidebar-body) {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--tf-bg);
}

/* шапка панели: заголовок и нормальная кнопка закрытия */
.ts-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
  padding: 14px 16px;
  border-bottom: 1px solid var(--tf-border);
  background: var(--tf-bg);

  &__title {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: var(--tf-text-strong);
  }
}

.ts-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid var(--tf-border);
  border-radius: 9px;
  background: var(--tf-bg);
  color: var(--tf-muted);
  font-size: 13px;
  cursor: pointer;
  transition: all .16s ease;

  &:hover {
    border-color: var(--tf-danger);
    background: var(--tf-danger-soft);
    color: var(--tf-danger);
  }
}

.sidebar-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  padding: 16px;
  color: var(--tf-text);
}

.sidebar-custom-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

/* поля формы в режиме form */
.sidebar-body ::v-deep {
  .form-group > label {
    margin-bottom: 5px;
    font-size: 12px;
    font-weight: 600;
    color: var(--tf-text);
  }

  .form-control,
  .custom-select {
    height: 40px;
    border: 1px solid var(--tf-border-strong);
    border-radius: 10px;
    background: var(--tf-bg);
    color: var(--tf-text);
    font-size: 13.5px;
    box-shadow: none;

    &:focus {
      border-color: var(--tf-primary);
      box-shadow: 0 0 0 3px var(--tf-primary-soft);
    }
  }

  textarea.form-control { height: auto; min-height: 84px; }
}

.ts-foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin: 18px -16px -16px;
  padding: 13px 16px;
  border-top: 1px solid var(--tf-border);
  background: var(--tf-soft);
}

.ts-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 38px;
  padding: 0 16px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  transition: all .16s ease;

  &--primary {
    border: 0;
    background: var(--tf-primary);
    color: #fff;

    &:hover { filter: brightness(1.07); }
  }

  &--ghost {
    border: 1px solid var(--tf-border-strong);
    background: var(--tf-bg);
    color: var(--tf-text);
    font-weight: 500;

    &:hover { border-color: var(--tf-primary); color: var(--tf-primary); }
  }
}

fieldset {
  border: none !important;
}

body.dark-only :deep(.b-sidebar) {
  @include tf-tokens-dark;
}
</style>
